import { useCallback, useMemo, useTransition } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";
import type { JobListFilters, JobSortOrder, SortDirection } from "~/features/dbpjkitWorkerJobs/types";
import { v2Route } from "~/v2/routes";
import { DbpjkitWorkerJobsPresenter } from "./dbpjkitWorkerJobs.presenter";

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORT: JobSortOrder = "createdAt";
const DEFAULT_DIRECTION: SortDirection = "desc";

export function DbpjkitWorkerJobsContainer() {
  const [params, setParams] = useTypedSearchParams(v2Route.dbpjkitWorkerJobs);
  const [isPending, startTransition] = useTransition();

  // The two text filters are persisted in the URL (v2 convention) so a refresh
  // restores them. Memoized so the reference is stable while params are unchanged —
  // the search form re-seeds its draft on every `filters` identity change.
  const filters: JobListFilters = useMemo(
    () => ({ commandName: params.commandName ?? "", spreadsheetId: params.spreadsheetId ?? "" }),
    [params.commandName, params.spreadsheetId],
  );

  const sortOrder: JobSortOrder = params.sortOrder ?? DEFAULT_SORT;
  const direction: SortDirection = params.sortOrderDirection ?? DEFAULT_DIRECTION;
  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const selectedJob = params.selectedDbpjkitWorkerJob ?? null;

  const onApplyFilters = useCallback(
    (next: JobListFilters) => {
      startTransition(() => {
        setParams((prev) => ({
          ...prev,
          commandName: next.commandName.trim() || undefined,
          spreadsheetId: next.spreadsheetId.trim() || undefined,
          page: undefined,
        }));
      });
    },
    [setParams],
  );

  const onClearFilters = useCallback(() => {
    startTransition(() => {
      setParams((prev) => ({ ...prev, commandName: undefined, spreadsheetId: undefined, page: undefined }));
    });
  }, [setParams]);

  const onSortingChange = useCallback(
    (nextOrder: JobSortOrder, nextDirection: SortDirection) => {
      startTransition(() => {
        setParams((prev) => ({
          ...prev,
          sortOrder: nextOrder === DEFAULT_SORT ? undefined : nextOrder,
          sortOrderDirection: nextDirection === DEFAULT_DIRECTION ? undefined : nextDirection,
          page: undefined,
        }));
      });
    },
    [setParams],
  );

  const onPageIndexChange = useCallback(
    (next: number) => {
      startTransition(() => {
        setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next }));
      });
    },
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) => {
      startTransition(() => {
        setParams((prev) => ({
          ...prev,
          pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next,
          page: undefined,
        }));
      });
    },
    [setParams],
  );

  const onSelect = useCallback(
    (id: string) => setParams((prev) => ({ ...prev, selectedDbpjkitWorkerJob: id })),
    [setParams],
  );
  const onCloseSelected = useCallback(
    () => setParams((prev) => ({ ...prev, selectedDbpjkitWorkerJob: undefined })),
    [setParams],
  );

  return (
    <DbpjkitWorkerJobsPresenter
      filters={filters}
      onApplyFilters={onApplyFilters}
      onClearFilters={onClearFilters}
      sortOrder={sortOrder}
      direction={direction}
      onSortingChange={onSortingChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      isPending={isPending}
      selectedJob={selectedJob}
      onSelect={onSelect}
      onCloseSelected={onCloseSelected}
    />
  );
}
